import { Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useState, useCallback, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import {
  userProfileState,
  currentRoleState,
  isPortalLoginState,
} from '../services/state.service';
import {
  UserProfile,
  Role,
  UserProfileDefaultValue,
  RoleDefaultValue,
} from '../types/user.types';
import UserService from '../services/user.service';
import EditButton from '../components/generic/EditButton';
import api from '../services/api.service';
import EditUsernameModal from '../components/EditUsernameModal';
import EditEmailModal from '../components/EditEmailModal';
import EditPasswordModal from '../components/EditPasswordModal';
import DeleteAccountModal from '../components/DeleteAccountModal';
import AlertModal from '../components/generic/AlertModal';
import BackToDashboard from '../components/generic/BackToDashboard';
import AuthService from '../services/auth.service';

function ManageAccount(): JSX.Element {
  const { t } = useTranslation();
  const [userProfile, setUserProfileState] = useRecoilState<UserProfile>(userProfileState);
  const [, setIsPortalLoginState] = useRecoilState(isPortalLoginState);
  const [, setCurrentRoleState] = useRecoilState<Role>(currentRoleState);
  const [showUsernameEdit, setShowUsernameEdit] = useState(false);
  const [showEmailEdit, setShowEmailEdit] = useState(false);
  const [userNameExist, setUserNameExist] = useState(false);
  const [emailExist, setEmailExist] = useState(false);
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const [showPasswordEdit, setShowPasswordEdit] = useState(false);
  const [showDeleteAccountDialogue, setShowDeleteAccountDialogue] = useState(false);
  const [deleteAccountErrorMessage, setDeleteAccountErrorMessage] = useState('');
  const [deleteAccountInvalid, setDeleteAccountInvalid] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [refresh, setRefresh] = useState(false);
  // State for dynamic AlertModal
  const [alertModalProps, setAlertModalProps] = useState({
    show: false,
    title: '',
    description: '',
    closeLabel: '',
    callback: () => {},
  });

  useEffect(() => {
    UserService.GetProfile().then((profile) => {
      setUserProfileState(profile);
    });
    setRefresh(false);
  }, [refresh]);

  function canDeleteAccount(roles: Role[]): boolean {
    const restrictedAccounts = ['SC', 'EP', 'COM', 'CPM', 'Adm'];
    if (roles.find((role) => restrictedAccounts.includes(role.abbreviation))) {
      return false;
    }
    return true;
  }

  const saveUserInfo = useCallback(async (result: boolean, data: any): Promise<void> => {
    if (!result) {
      setShowUsernameEdit(false);
      setShowEmailEdit(false);
      setShowPasswordEdit(false);
      return;
    }

    let payload = {};
    if (data.type === 'username') {
      payload = {
        username: data.username,
        type: 'username',
      };
    }
    if (data.type === 'email') {
      payload = {
        email: data.email,
        type: 'email',
      };
    }
    if (data.type === 'password') {
      payload = {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        type: 'password',
      };
    }

    try {
      const response: any = await api.post('/updateuserprofile/', payload);

      if (response.status === 200) {
        if (data.type === 'username') {
          setShowUsernameEdit(false);
          setAlertModalProps({
            show: true,
            title: t('UserName_Update_Success'),
            description: t('UserName_Update_Success_Description'),
            closeLabel: t('Return_to_Manage_Account'),
            callback: () => setAlertModalProps((prev) => ({ ...prev, show: false })),
          });
          setRefresh(true);
        }
        if (data.type === 'email') {
          setShowEmailEdit(false);
          setAlertModalProps({
            show: true,
            title: t('Email_Update_Success'),
            description: t('Email_Update_Success_Description'),
            closeLabel: t('Return_to_Manage_Account'),
            callback: () => setAlertModalProps((prev) => ({ ...prev, show: false })),
          });
          setRefresh(true);
        }
        if (data.type === 'password') {
          setShowPasswordEdit(false);
          setAlertModalProps({
            show: true,
            title: t('Password_Update_Success'),
            description: t('Password_Update_Success_Description'),
            closeLabel: t('Return_to_Manage_Account'),
            callback: () => setAlertModalProps((prev) => ({ ...prev, show: false })),
          });
          setRefresh(true);
        }
      }
    } catch (error: any) {
      if (error.response.data.error === 'Duplicate_Username') {
        setUserNameExist(true);
      }
      if (error.response.data.error === 'Duplicate_Email') {
        setEmailExist(true);
      }
      if (error.response.data.error === 'Current_Password_Invalid') {
        setPasswordInvalid(true);
        setPasswordErrorMessage(t(`${error.response.data.error}`));
      }
      if (error.response.data.error === 'Password_Strength_Error') {
        setPasswordInvalid(true);
        setPasswordErrorMessage(t(`${error.response.data.error}`));
      }
      if (error.response.data.error === 'Same_Password_Error') {
        setPasswordInvalid(true);
        setPasswordErrorMessage(t(`${error.response.data.error}`));
      }
    }
  }, []);

  const deleteUserAccount = useCallback(async (result: boolean, data: any): Promise<void> => {
    if (!result) {
      setShowDeleteAccountDialogue(false);
      setDeleteAccountInvalid(false);
      return;
    }

    const payload = {
      username: data.username,
      password: data.password,
    };

    try {
      const response: any = await api.post('/deleteuserprofile/', payload);
      if (response.status === 200) {
        setShowDeleteAccountDialogue(false);
        setAlertModalProps({
          show: true,
          title: t('Account_Deleted'),
          description: t('Account_Deleted_Description'),
          closeLabel: t('ReturnToLogin'),
          callback: () => {
            setAlertModalProps((prev) => ({ ...prev, show: false }));
            AuthService.Logout();
            setIsPortalLoginState(false);
            setUserProfileState(UserProfileDefaultValue);
            setCurrentRoleState(RoleDefaultValue);
            window.location.href = '/';
          },
        });
      }
    } catch (error: any) {
      if (error.response.data.error === 'Current_Password_Invalid') {
        setDeleteAccountInvalid(true);
        setDeleteAccountErrorMessage(t(`${error.response.data.error}`));
      }
      if (error.response.data.error === 'Username_Invalid') {
        setDeleteAccountInvalid(true);
        setDeleteAccountErrorMessage(t(`${error.response.data.error}`));
      }
    }
  }, []);

  return (
    <div className="container-fluid mb-5">
      <AlertModal
        show={alertModalProps.show}
        title={alertModalProps.title}
        description={alertModalProps.description}
        closeLabel={alertModalProps.closeLabel}
        callback={alertModalProps.callback}
      />
      <EditUsernameModal
        show={showUsernameEdit}
        currentUsername={userProfile.username}
        saveUserInfo={saveUserInfo}
        usernameExist={userNameExist}
        setUserNameExist={setUserNameExist}
      />
      <EditEmailModal
        show={showEmailEdit}
        currentEmail={userProfile.email}
        saveUserInfo={saveUserInfo}
        emailExist={emailExist}
        setEmailExist={setEmailExist}
      />
      <EditPasswordModal
        show={showPasswordEdit}
        saveUserInfo={saveUserInfo}
        passwordInvalid={passwordInvalid}
        passwordErrorMessage={passwordErrorMessage}
        setPasswordInvalid={setPasswordInvalid}
      />
      <DeleteAccountModal
        show={showDeleteAccountDialogue}
        deleteUserAccount={deleteUserAccount}
        deleteAccountInvalid={deleteAccountInvalid}
        deleteAccountErrorMessage={deleteAccountErrorMessage}
        setDeleteAccountInvalid={setDeleteAccountInvalid}
      />
      <div className="mt-3">
        <BackToDashboard />
      </div>
      <h4 className="mx-auto mt-3 text-center">
        <div className="title">{t('Manage_Account')}</div>
      </h4>
      <div className="row">
        <div>
          <Form className="mx-auto col-6">
            <Form.Group>
              <div className="label-edit-container mt-3">
                <Form.Label className="mb-0">
                  {t('Username')}
                </Form.Label>
                <EditButton
                  label=""
                  handler={() => {
                    setShowUsernameEdit(true);
                  }}
                />
              </div>
              <Form.Control
                value={userProfile.username}
                readOnly
              />
            </Form.Group>
            <Form.Group>
              <div className="label-edit-container mt-3">
                <Form.Label className="mb-0">
                  {t('Email')}
                </Form.Label>
                <EditButton
                  label=""
                  handler={() => {
                    setShowEmailEdit(true);
                  }}
                />
              </div>
              <Form.Control
                value={userProfile.email}
                readOnly
              />
              <Button
                className="mb-3 mx-4 mt-4 ms-auto createbutton"
                variant="primary"
                size="sm"
                active
                onClick={() => setShowPasswordEdit(true)}
              >
                {`${t('Update_Password')}`}
              </Button>
              {canDeleteAccount(userProfile.roles) && (
                <Button
                  className="mb-3 mt-4 ms-auto createbutton"
                  variant="danger"
                  size="sm"
                  active
                  onClick={() => setShowDeleteAccountDialogue(true)}
                >
                  {`${t('Delete_Account')}`}
                </Button>
              )}
            </Form.Group>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default ManageAccount;
